import type React from "react";

import {useSettingsStore} from "@project/app-state";
import {AppLayout, ThemeBuilderToggle} from "@project/design-system-web";
import {useRouter} from "@tanstack/react-router";

import {ChromeOverlayProvider} from "./chrome-overlay-provider";
import {AppHeader} from "./header/app-header";
import {AppNavigationSidebar} from "./left-sidebar/app-navigation-sidebar";
import {ShellConfirmProvider} from "./shell-confirm-provider";
import {WorkspaceSplit} from "./workspace-split";

interface AppShellProps {
  children: React.ReactNode;
}

/**
 * Authenticated application shell.
 *
 * HeroUI AppLayout owns the global scaffold: left navigation sidebar, sticky
 * header, and the main region. The product-specific workspace split remains
 * inside the main region so the app content portal can stay scoped to the
 * content cell and never include header/sidebar/extension chrome.
 */
export function AppShell({children}: AppShellProps) {
  const isDevModeEnabled = useSettingsStore((s) => s.isDevModeEnabled);
  const router = useRouter();

  return (
    <div className="bg-background text-foreground h-dvh w-full overflow-hidden">
      <ChromeOverlayProvider>
        <ShellConfirmProvider>
          <AppLayout
            className="h-full min-h-0"
            defaultSidebarOpen
            navbar={<AppHeader />}
            navigate={(href: string) => void router.navigate({to: href})}
            scrollMode="content"
            sidebar={<AppNavigationSidebar />}
            sidebarCollapsible="icon"
            toggleShortcut="mod+b"
          >
            <WorkspaceSplit>{children}</WorkspaceSplit>
          </AppLayout>

          {/* Global Theme Builder overlay — only mounted when dev mode is active */}
          {isDevModeEnabled && <ThemeBuilderToggle />}
        </ShellConfirmProvider>
      </ChromeOverlayProvider>
    </div>
  );
}
